import { Badge } from "@devhop/ui";
import { FolderOpen, LayoutGrid, Tag } from "lucide-react";
import type { ProductDTO } from "../api/client";

type ProductDetailInfoProps = {
  product: ProductDTO;
};

function getDisplayName(
  name: { lo?: string; en?: string; th?: string } | undefined,
): string {
  if (!name) return "—";
  return name.lo || name.en || name.th || "—";
}

function descriptionLo(product: ProductDTO): string {
  return (product.description as { lo?: string })?.lo ?? "—";
}

function statusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive"> = {
    draft: "secondary",
    active: "default",
    archived: "destructive",
  };
  const labels: Record<string, string> = {
    draft: "ແບບຮ່າງ",
    active: "ເປີດໃຊ້ງານ",
    archived: "ເກັບຖາວອນ",
  };
  return (
    <Badge variant={variants[status] ?? "default"}>
      {labels[status] ?? status}
    </Badge>
  );
}

export function ProductDetailInfo({ product }: ProductDetailInfoProps) {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Left Column - Product Information (2/3) */}
      <div className="lg:col-span-2">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-4 font-semibold text-lg">ຂໍ້ມູນສິນຄ້າ</h2>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-muted-foreground">Slug</div>
              <div className="col-span-2">
                <code className="rounded bg-muted px-2 py-1 font-mono text-xs">
                  {product.slug}
                </code>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-muted-foreground">ສະຖານະ</div>
              <div className="col-span-2">{statusBadge(product.status)}</div>
            </div>

            {product.options && product.options.length > 0 && (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-muted-foreground">ຕົວເລືອກ</div>
                <div className="col-span-2 space-y-2">
                  {product.options.map((opt) => (
                    <div key={opt.id}>
                      <span className="font-medium">
                        {(opt.name as { lo?: string })?.lo ??
                          (opt.name as { en?: string })?.en ??
                          opt.slug}
                        :
                      </span>{" "}
                      {opt.values && opt.values.length > 0 ? (
                        <span className="text-muted-foreground">
                          {opt.values
                            .map(
                              (v) =>
                                (v.value as { lo?: string })?.lo ??
                                (v.value as { en?: string })?.en ??
                                (v.value as { th?: string })?.th ??
                                "—",
                            )
                            .join(", ")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {descriptionLo(product) !== "—" && (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-muted-foreground">ລາຍລະອຽດ</div>
                <div className="col-span-2 text-muted-foreground">
                  {descriptionLo(product)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column - Categories, Collections, Tags */}
      <div className="space-y-4 lg:col-span-1">
        {/* Categories */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-3 flex items-center gap-2 font-semibold text-muted-foreground text-sm">
            <FolderOpen className="h-4 w-4" />
            ຫມວດມູ່
          </h2>
          {product.categories && product.categories.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {product.categories.map((cat) => (
                <Badge
                  key={cat.id}
                  variant="secondary"
                  className="gap-1.5 px-3 py-1 font-normal"
                >
                  {getDisplayName(
                    cat.name as { lo?: string; en?: string; th?: string },
                  )}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ໄດ້ກຳນົດຫມວດມູ່</p>
          )}
        </div>

        {/* Collections */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-3 flex items-center gap-2 font-semibold text-muted-foreground text-sm">
            <LayoutGrid className="h-4 w-4" />
            ຄໍເລັກຊັນ
          </h2>
          {product.collections && product.collections.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {product.collections.map((coll) => (
                <Badge
                  key={coll.id}
                  variant="outline"
                  className="gap-1.5 border-primary/30 px-3 py-1 font-normal text-primary"
                >
                  {getDisplayName(
                    coll.name as { lo?: string; en?: string; th?: string },
                  )}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ໄດ້ກຳນົດຄໍເລັກຊັນ</p>
          )}
        </div>

        {/* Tags */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="mb-3 flex items-center gap-2 font-semibold text-muted-foreground text-sm">
            <Tag className="h-4 w-4" />
            Tag
          </h2>
          {product.tags && product.tags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <Badge
                  key={t.id}
                  variant="secondary"
                  className="gap-1.5 bg-muted/80 px-3 py-1 font-normal"
                >
                  #
                  {getDisplayName(
                    t.name as { lo?: string; en?: string; th?: string },
                  )}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີ tag</p>
          )}
        </div>
      </div>
    </div>
  );
}
